"use client";

import { useState, useSyncExternalStore } from "react";

import { clienteAuth } from "@/lib/cliente-auth";

import { percorsoDiRitorno } from "./percorso-di-ritorno";

/**
 * «Entra con il volto o l'impronta»: la seconda porta, **accanto** al modulo
 * della password e mai al suo posto.
 *
 * **Nasce nascosto, e lo rivela il codice del browser.** Non è una rifinitura:
 * questa pagina è stata riscritta il 6 settembre 2026 apposta per funzionare
 * **senza** codice cliente, dopo che l'accesso rimbalzava da Edge e da Safari.
 * Una chiave d'accesso invece il codice cliente lo pretende — in mezzo c'è
 * `navigator.credentials` — quindi la regola è che se quel codice non parte, la
 * pagina deve restare **indistinguibile da prima**: modulo, campi, pulsante
 * «Entra», e nient'altro. Un pulsante disegnato dal server e poi inerte sarebbe
 * un pulsante che non fa niente su un dispositivo che non lo capisce.
 *
 * Per la stessa ragione si controlla anche che il browser sappia cosa sono le
 * chiavi d'accesso (`window.PublicKeyCredential`): su un browser vecchio il
 * pulsante non compare affatto, invece di comparire e fallire.
 *
 * **Se non funziona lo dice in italiano.** È la stessa regola del pulsante
 * «copia» dell'attivazione del secondo fattore: hardware assente, Bluetooth
 * spento, dito appoggiato male, o semplicemente «ho cambiato idea» — in tutti i
 * casi la frase dice cosa fare adesso, che è scrivere la password qui sotto. Un
 * pulsante che resta a girare a vuoto è peggio di un pulsante assente.
 */
/**
 * «Questo browser sa cosa sono le chiavi d'accesso?»
 *
 * Sono le tre funzioni che `useSyncExternalStore` vuole, e sono così perché la
 * risposta **non cambia mai** durante la vita della pagina: non c'è niente a
 * cui iscriversi, quindi disiscriversi è non fare niente. Sul server la
 * risposta è sempre «no», ed è quella la riga che fa nascere il pulsante
 * nascosto.
 *
 * Si fa così e non con uno stato acceso da un effetto perché un `setState`
 * dentro un effetto è una resa in più su ogni apertura della pagina di accesso,
 * e perché questa non è una preferenza da ricordare: è una domanda
 * sull'ambiente, che è esattamente il mestiere di questa funzione.
 */
const NIENTE_A_CUI_ISCRIVERSI = () => () => {};
const CHIAVI_NEL_BROWSER = () => "PublicKeyCredential" in window;
const CHIAVI_SUL_SERVER = () => false;

export function PulsanteChiaveAccesso({ torna }: { torna: string }) {
  const disponibile = useSyncExternalStore(
    NIENTE_A_CUI_ISCRIVERSI,
    CHIAVI_NEL_BROWSER,
    CHIAVI_SUL_SERVER,
  );
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function entra() {
    setErrore(null);
    setInCorso(true);

    const risposta = await clienteAuth.signIn.passkey();

    if (risposta?.error) {
      const codice = (risposta.error as { code?: unknown }).code;
      setErrore(messaggioDellaChiave(typeof codice === "string" ? codice : undefined));
      setInCorso(false);
      return;
    }

    /*
     * Dove si torna. Il valore arriva già ripulito dal server — la pagina lo fa
     * passare da `percorsoDiRitorno` prima di disegnarlo — e qui ci ripassa una
     * seconda volta: è la stessa funzione, costa niente, e vuol dire che la
     * garanzia «non si esce mai da StudyHub» si legge **in questo file**, senza
     * dover risalire a chi ha passato la proprietà.
     *
     * È una navigazione vera e non un `router.push`: il cookie di sessione è
     * appena arrivato, e ricaricare la pagina per intero è il modo in cui tutti
     * i browser si comportano allo stesso modo — la lezione del 6 settembre.
     */
    window.location.assign(percorsoDiRitorno(torna));
  }

  if (!disponibile) {
    return null;
  }

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <button type="button" className="bottone-secondario" onClick={entra} disabled={inCorso}>
        {inCorso ? "Aspetto il dispositivo…" : "Entra con il volto o l'impronta"}
      </button>
      <p className="campo-aiuto" style={{ marginTop: "0.5rem" }}>
        Funziona se hai registrato una chiave d&apos;accesso su questo dispositivo, da «Il mio
        account». Se non l&apos;hai fatto, entra con la password qui sopra.
      </p>

      {errore !== null && (
        <p className="stato stato-errore messaggio" role="alert">
          <span className="stato-segno" aria-hidden="true" />
          {errore}
        </p>
      )}
    </div>
  );
}

/**
 * Le risposte del dispositivo, in italiano.
 *
 * Tutte finiscono nello stesso posto — «entra con la password qui sopra» —
 * perché è l'unica cosa da fare, e perché questa è la pagina di accesso: chi ci
 * arriva vuole entrare, non capire.
 */
function messaggioDellaChiave(codice: string | undefined): string {
  switch (codice) {
    case "ERROR_CEREMONY_ABORTED":
    case "AUTH_CANCELLED":
      return "Su questo dispositivo non ha funzionato, oppure hai annullato: entra con la password qui sopra.";

    case "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY":
    case "ERROR_INVALID_DOMAIN":
      return "Questo dispositivo non riesce a usare la chiave d'accesso per StudyHub: entra con la password qui sopra.";

    default:
      return "Non sono riuscito a entrare con la chiave d'accesso: entra con la password qui sopra. Se su questo dispositivo non hai mai registrato una chiave, è normale.";
  }
}
